import React, { useState, useEffect } from 'react'
import { ShieldCheck, Ticket, CheckCircle, Ban, Clock, Package, Pencil, AlertTriangle } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import Pagination from '../../components/admin/Pagination'

const AuditLog = () => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    action: '',
    page: 1,
    limit: 50,
  })
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const api = useApi()

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const params = { page: filters.page, limit: filters.limit }
      if (filters.action) params.action = filters.action
      const data = await api.getAuditLog(params)
      setLogs(data.logs || [])
      setTotalPages(data.total_pages || 1)
      setTotalCount(data.total || 0)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchLogs() }, [filters])

  const formatDate = (d) => {
    if (!d) return '—'
    return new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' })
  }

  const actionColors = {
    code_created: 'text-success',
    code_redeemed: 'text-accent',
    code_revoked: 'text-error',
    code_expired: 'text-warning',
    failed_attempt: 'text-error',
    admin_login: 'text-accent',
    product_created: 'text-success',
    product_updated: 'text-warning',
  }

  const getActionIcon = (action) => {
    const icons = {
      admin_login: <ShieldCheck size={18} className="text-accent" />,
      code_created: <Ticket size={18} className="text-success" />,
      code_redeemed: <CheckCircle size={18} className="text-accent" />,
      code_revoked: <Ban size={18} className="text-error" />,
      code_expired: <Clock size={18} className="text-warning" />,
      failed_attempt: <AlertTriangle size={18} className="text-error" />,
      product_created: <Package size={18} className="text-success" />,
      product_updated: <Pencil size={18} className="text-warning" />,
    }
    
    return icons[action] || <Package size={18} className="text-text-muted" />
  }

  const getActionLabel = (action) => {
    const labels = {
      admin_login: 'Admin Login',
      code_created: 'Code Created',
      code_redeemed: 'Code Redeemed',
      code_revoked: 'Code Revoked',
      code_expired: 'Code Expired',
      failed_attempt: 'Failed Attempt',
      product_created: 'Product Created',
      product_updated: 'Product Updated',
    }
    
    return labels[action] || action?.replace(/_/g, ' ') || 'Unknown Action'
  }

  const actions = [
    'admin_login', 'code_created', 'code_redeemed', 'code_revoked', 
    'code_expired', 'failed_attempt', 'product_created', 'product_updated'
  ]

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-2xl font-bold text-text-primary">Audit Log</h1>

      {/* Filters */}
      <div className="card">
        <select
          value={filters.action}
          onChange={(e) => setFilters(f => ({ ...f, action: e.target.value, page: 1 }))}
          className="input w-full sm:w-64"
        >
          <option value="">All Actions</option>
          {actions.map(a => (
            <option key={a} value={a}>{getActionLabel(a)}</option>
          ))}
        </select>
      </div>

      {/* Log entries */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-text-muted">No audit entries found</div>
        ) : (
          <>
            <div className="divide-y divide-border">
              {logs.map((log) => (
                <div key={log.id} className="px-4 py-4 hover:bg-surface/30 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="mt-1">
                        {getActionIcon(log.action)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`text-sm font-medium ${actionColors[log.action] || 'text-text-primary'}`}>
                            {getActionLabel(log.action)}
                          </span>
                          {log.entity_type && (
                            <span className="text-text-muted text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {log.entity_type} #{log.entity_id}
                            </span>
                          )}
                        </div>
                        <div className="text-text-muted text-xs mb-2">
                          <span className="font-medium">Actor:</span> {log.actor || 'system'}
                          {log.ip_address && (
                            <>
                              <span className="mx-2">•</span>
                              <span className="font-medium">IP:</span> {log.ip_address}
                            </>
                          )}
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2">
                            <details className="group">
                              <summary className="text-xs text-text-muted cursor-pointer hover:text-text-secondary">
                                Show details
                              </summary>
                              <div className="mt-2 text-xs text-text-muted font-mono bg-gray-50 border border-gray-200 rounded px-3 py-2 overflow-x-auto">
                                <pre>{JSON.stringify(log.details, null, 2)}</pre>
                              </div>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="text-text-muted text-xs whitespace-nowrap ml-4 mt-1">
                      {formatDate(log.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <Pagination
              page={filters.page}
              totalPages={totalPages}
              total={totalCount}
              limit={filters.limit}
              onPageChange={(p) => setFilters(f => ({ ...f, page: p }))}
              onLimitChange={(l) => setFilters(f => ({ ...f, limit: l, page: 1 }))}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default AuditLog